import { AdvertiserPayoutRequests } from "components/Report/UI/Dashboard/cards/AdvertiserPayoutRequests";
import { AffiliatePayoutRequests } from "components/Report/UI/Dashboard/cards/AffiliatePayoutRequests";
import { BestAffiliates } from "components/Report/UI/Dashboard/cards/BestAffiliates";
import { BestOffers } from "components/Report/UI/Dashboard/cards/BestOffers";
import { DailyOverview } from "components/Report/UI/Dashboard/cards/DailyOverview";
import { LatestTransactions } from "components/Report/UI/Dashboard/cards/LatestTransactions";
import { OfferRequests } from "components/Report/UI/Dashboard/cards/OfferRequests";
import { UniqueLink } from "components/Report/UI/Dashboard/cards/UniqueLink.tsx";
import { WorkspaceRequests } from "components/Report/UI/Dashboard/cards/WorkspaceRequests";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions";
import { ReactNode, useMemo } from "react";
import { PermissionEnum } from "types/enums";

const title = (label: string, to?: string) => <TenantLink to={to}>{label}</TenantLink>;

export interface CardDef {
    id: string;
    title: ReactNode;
    subtitle?: string;
    node: ReactNode;
    permission: PermissionEnum;
    extra?: ReactNode;
    draggable?: boolean;
}

export const useCardRegistry = (): CardDef[] => {
    const { hasPermission } = usePermission();
    const { t } = usePaldockTranslation("ui.dashboard.cards");

    return useMemo(() => {
        const allCards: CardDef[] = [
            {
                id: "workspace-requests",
                title: title(t("workspace-requests.title"), "/affiliate"),
                node: hasPermission(PermissionEnum.DashboardWorkspaceRequestsView) ? <WorkspaceRequests /> : null,
                permission: PermissionEnum.DashboardWorkspaceRequestsView,
            },
            {
                id: "offer-requests",
                title: title(t("offer-requests.title"), "/affiliate/request/offer"),
                node: hasPermission(PermissionEnum.DashboardOfferRequestsView) ? <OfferRequests /> : null,
                permission: PermissionEnum.DashboardOfferRequestsView,
            },
            {
                id: "affiliate-payouts",
                title: title(t("affiliate-payout-requests.title"), "/affiliate/request/payout"),
                node: hasPermission(PermissionEnum.DashboardAffiliatePayoutsView) ? <AffiliatePayoutRequests /> : null,
                permission: PermissionEnum.DashboardAffiliatePayoutsView,
            },
            {
                id: "advertiser-payouts",
                title: title(t("advertiser-payout-requests.title"), "/advertiser/request/payout"),
                node: hasPermission(PermissionEnum.DashboardAdvertiserPayoutsView) ? (
                    <AdvertiserPayoutRequests />
                ) : null,
                permission: PermissionEnum.DashboardAdvertiserPayoutsView,
            },

            {
                id: "daily-overview",
                title: title(t("daily-overview.title"), "/report/performance"),
                node: hasPermission(PermissionEnum.DashboardDailyOverviewView) ? <DailyOverview /> : null,
                permission: PermissionEnum.DashboardDailyOverviewView,
            },
            {
                id: "latest-transactions",
                title: title(t("latest-transactions.title"), "/report/transaction"),
                node: hasPermission(PermissionEnum.DashboardLatestConversionsView) ? <LatestTransactions /> : null,
                permission: PermissionEnum.DashboardLatestConversionsView,
            },
            {
                id: "best-affiliates",
                title: title(t("best-affiliates.title"), "/affiliate"),
                node: hasPermission(PermissionEnum.DashboardTopPartnerView) ? <BestAffiliates /> : null,
                permission: PermissionEnum.DashboardTopPartnerView,
            },
            {
                id: "best-offers",
                title: title(t("best-offers.title"), "/offer"),
                node: hasPermission(PermissionEnum.DashboardTopOfferView) ? <BestOffers /> : null,
                permission: PermissionEnum.DashboardTopOfferView,
            },
            // Invite Partners: keep hidden until notifications exist
            // {
            //   id: "invite-partners",
            //   title: title(t("invite-partners.title")),
            //   node: hasPermission(PermissionEnum.DashboardInvitePartnersView) ? <InvitePartners /> : null,
            //   permission: PermissionEnum.DashboardInvitePartnersView,
            //   draggable: false,
            // },
            {
                id: "unique-link",
                title: t("unique-link.title"),
                node: hasPermission(PermissionEnum.DashboardUniqueLinkView) ? <UniqueLink /> : null,
                permission: PermissionEnum.DashboardUniqueLinkView,
                draggable: false,
            },

            // "Unique link" card removed per request (Odstranit tuto linku)
        ];

        return allCards.filter((card) => hasPermission(card.permission));
    }, [hasPermission, t]);
};
